import axios from 'axios';
import { REACT_APP_TEAMCAMP_APIURL } from '../global/environment';
import { captureException } from '@sentry/react';
import { getAPIErrorReason } from '../helpers/common';
import { rootStore } from '../mobx/rootStore';
import { formatToViewStore } from '../mobx/common';
import { RawView, CreateViewPayload, UpdateViewPayload, UpdateViewUsersPayload } from '../mobx/interfaces/view';

/**
 * @desc Project - tasks - create new task
 * @param {*}
 */
export const createNewView = async (payload: CreateViewPayload) => {
  try {
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/views/createView`, payload);
    const { data } = response;
    if (data) {
      return data;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to create view please try again';
    captureException(error);
    dispatchViewError(error);
    return false;
  }
};

/**
 * @desc View - Get view list
 */
export const getViewList = async () => {
  try {
    rootStore.viewStore.setLoading(true);
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/views/getViews`);
    if (response?.data) {
      const activeViews = response.data.filter((view: { IsArchived: boolean }) => !view.IsArchived);
      const formattedViews = formatToViewStore(activeViews);
      rootStore.viewStore.setViews(formattedViews as RawView[]);
      rootStore.viewStore.updateViewPreferenceFromFirebase();
      return formattedViews;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get view list please try again';
    captureException(error);
    dispatchViewError(error);
    return false;
  } finally {
    rootStore.viewStore.setLoading(false);
  }
};

/* @desc update view data
 * @param {*}viewId
 */
export const updateView = async (payload: UpdateViewPayload, viewId: string) => {
  try {
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/views/updateView/${viewId}`, payload);
    if (response) {
      dispatchViewSuccess('View updated successfully');
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update view data please try again';
    captureException(error);
    dispatchViewError(error);
    return false;
  }
};

/**
 * @desc View - Remove view
 * @param {*}
 */
export const removeView = async (viewId: string) => {
  try {
    rootStore.viewStore.setLoading(true);
    const response = await axios.delete(`${REACT_APP_TEAMCAMP_APIURL}/views/removeView/${viewId}`);
    if (response) {
      dispatchViewSuccess('View removed successfully');
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to remove view please try again';
    captureException(error);
    dispatchViewError(error);
    return false;
  } finally {
    rootStore.viewStore.setLoading(false);
  }
};

/**
 * @desc View - Add user in view
 * @param {*}
 */
export const addViewUser = async (payload: UpdateViewUsersPayload | string[], selectedViewId?: string) => {
  try {
    if (!selectedViewId) return false;
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/views/${selectedViewId}/addUserInViews`, payload);

    const { data } = response;
    rootStore.alertNotificationStore.setSuccessMessage(data.message);
    getViewList();
  } catch (e) {
    const error = getAPIErrorReason(e) || 'unable to add or remove user';
    rootStore.alertNotificationStore.setErrorMessage(error);
  }
};

/**
 * @desc View - Remove user from view
 * @param {*}
 */
export const removeViewUser = async (payload: UpdateViewUsersPayload | string[], selectedViewId?: string) => {
  try {
    if (!selectedViewId) return false;
    const response = await axios.put(
      `${REACT_APP_TEAMCAMP_APIURL}/views/${selectedViewId}/removeUserFromViews`,
      payload
    );
    const { data } = response;
    rootStore.alertNotificationStore.setSuccessMessage(data.message);
    getViewList();
  } catch (e) {
    const error = getAPIErrorReason(e) || 'unable to add or remove user';
    rootStore.alertNotificationStore.setErrorMessage(error);
  }
};

export function dispatchViewError(msg: string) {
  rootStore.alertNotificationStore.setErrorMessage(msg);
}

export function dispatchViewSuccess(msg: string) {
  rootStore.alertNotificationStore.setSuccessMessage(msg);
}
